import { Component, ElementRef, Renderer2, forwardRef, Inject, ViewChild } from '@angular/core';
import { select } from '@angular-redux/store';
import { takeUntil } from 'rxjs/operators';
import { Observable ,  Subject } from 'rxjs';
import { FeedbackPopupComponent } from './feedback-popup.component';
import { FeedbackSentComponent } from './feedback-sent/feedback-sent.component';
import { BaseComponent } from 'sharedFeatures/base';
import { CheckboxComponent, RadioGroupComponent, RadioItem } from 'sharedFeatures/form';
import { accountActions } from 'sharedFeatures/store';
import { AuthData, CommonApp } from 'sharedFeatures/sharedModels';
import { CustomerService } from 'sharedFeatures/sharedServices';
import { SharedGuiSmallModule } from '../../../../sharedGuiSmall/sharedGuiSmall.module';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'feedback-popup-body',
    templateUrl: './feedback-popup-body.component.html',
    styleUrls: ['./feedback-popup-body.component.less'],
    standalone: true,
    imports: [SharedGuiSmallModule, CheckboxComponent, RadioGroupComponent, TranslateModule],
    providers: [CustomerService]
})
export class FeedbackPopupBodyComponent extends BaseComponent {

    //feedbackAnonymousLabelText = $localize`Keep my feedback anonymous`;
    //submitFeedbackText = $localize`Submit feedback`;
    priceText = $localize`Price`;
    missingFeatureText = $localize`Missing feature`;
    notSureWhichPackageText = $localize`Not sure which package`;
    imStillTryingProductText = $localize`I'm still trying the product`;
    otherPleaseDescribeText = $localize`Other (please describe)`;

    @ViewChild('commentArea')
    private commentArea!: ElementRef<HTMLTextAreaElement>;

    radioItems: Array<RadioItem> = [
        new RadioItem(this.priceText, this.priceText, 'feedbackPriceItem'),
        new RadioItem(this.missingFeatureText, this.missingFeatureText, 'feedbackMissingFeatureItem'),
        new RadioItem(this.notSureWhichPackageText, this.notSureWhichPackageText, 'feedbackNotSureItem'),
        new RadioItem(this.imStillTryingProductText, this.imStillTryingProductText, 'feedbackStillTryingItem'),
        new RadioItem(this.otherPleaseDescribeText, this.otherPleaseDescribeText, 'feedbackOtherItem')
    ];

    feedbackChoice: string = '';
    feedbackComment: string = '';
    feedbackAnonymous: boolean = false;

    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    /*************** main_app store **********************/
    @select(['appData', 'appComponent'])
    readonly appComponent$!: Observable<any>;
    private appComponent: any = null;
    /*****************************************************/

    /*************** account_app store *******************/
    @select(['accountAppData', 'accountAppComponent'])
    readonly accountAppComponent$!: Observable<any>;
    private accountAppComponent: any = null;
    /*****************************************************/

    @select(['account', 'authData'])
    readonly authData$!: Observable<AuthData>;
    authData: AuthData = new AuthData();

    @select(['account', 'UIState'])
    readonly UIState$!: Observable<any>;
    private UIState: any;

    constructor(
        @Inject(forwardRef(() => FeedbackPopupComponent)) private parent: FeedbackPopupComponent,
        el: ElementRef,
        r: Renderer2,
        private customerService: CustomerService
    ) {
        super(el, r);
    }

    ngOnInit() {
        // Subscribe on the redux store
        this.subscribeReduxStore();
    }

    ngOnDestroy() {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }

    // Subscribe on the redux store
    private subscribeReduxStore(): void {
        this.appComponent$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe((x: any) => this.appComponent = x);

        this.accountAppComponent$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe((x: any) => this.accountAppComponent = x);

        this.authData$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe((x: AuthData) => this.authData = x);

        this.UIState$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe((x: any) => this.UIState = x);
    }

    onClose(): void {
        this.parent.close();
    }

    handleChoiceRadioChange(ev: Event): void {
        if (!ev) return;

        window.setTimeout(() => {
            if (this.commentArea)
                (this.commentArea.nativeElement as HTMLTextAreaElement).focus();
        }, 0);
    }

    handleSubmitClick(): void {
        if (!this.authData) {
            console.error('authData is empty');
            return;
        }

        this.customerService.sendFeedback(
            this.authData.userID,
            this.feedbackChoice,
            this.feedbackComment,
            this.feedbackAnonymous)
            .then(() => {
                // Change UIState and write it to redux
                this.UIState = Object.assign({}, this.UIState, { FeedbackPopup: { submitted: 'true' } });
                accountActions.setUIState(this.UIState);

                this.parent.close();

                // Show thanks popup
                this.showFeedbackSentPopup();
            });
    }

    getApp(): CommonApp {
        if (this.appComponent)
            return this.appComponent;
        else if (this.accountAppComponent)
            return this.accountAppComponent;

        throw new Error('Architecture error.');
    }
    
    private showFeedbackSentPopup(): void {
        let popup: FeedbackSentComponent = <FeedbackSentComponent>this.getApp().createDialog(FeedbackSentComponent);;
        const x = (document.body.clientWidth - popup.width) / 2;
        const y = 40;
        popup.show(x, y);
    }
}
